import { InvoiceListParams } from "../../types/finance/FinanceTypes";
import type { UserUuid } from "../../types/identity";

/**
 * React Query keys for the invoicing screens, in one place.
 *
 * Every key is prefixed `["finance", …]` so a mutation can invalidate the whole area with one
 * call. That matters here more than usual: recording a payment changes the invoice row, its
 * ledger AND its position in a status-filtered list, and an invalidation that misses one of the
 * three leaves an operator looking at a stale balance on a document they are about to send.
 */
export const financeKeys = {
	all: ["finance"] as const,

	invoices: () => [...financeKeys.all, "invoices"] as const,
	invoiceList: (params: InvoiceListParams) => [...financeKeys.invoices(), params] as const,

	invoice: (id: string) => [...financeKeys.all, "invoice", id] as const,
	invoicePayments: (id: string) => [...financeKeys.all, "invoice-payments", id] as const,

	clinicSettings: () => [...financeKeys.all, "clinic-settings"] as const,

	/** Patient labels for a page of invoices — keyed on the ids so it re-fetches per page. */
	invoicePatients: (ids: string[]) => [...financeKeys.all, "invoice-patients", ids] as const,

	/**
	 * The line-item picker's catalogue reads (S7/S11).
	 *
	 * The product key carries the search term AND the page: the response is one page of a
	 * term-filtered list, and a key that omitted either would serve page 1 of "vitamin" to a picker
	 * asking for page 2 of "peptide". ⚠️ It also carries a live stock count, so this is the one
	 * finance query that should NOT be given a long `staleTime` — a practitioner reading "12
	 * available" off a minute-old cache is reading a number that may already be wrong.
	 */
	catalogueProducts: (search?: string, page?: number) =>
		[...financeKeys.all, "catalogue-products", (search ?? "").trim(), page ?? 1] as const,

	/**
	 * Keyed on the practitioner because the SAME appointment type has a different price per
	 * practitioner (`custom_price`, decision D7) — one shared key would serve one practitioner
	 * another's negotiated price.
	 */
	catalogueAppointmentTypes: (practitionerId?: UserUuid) =>
		[...financeKeys.all, "catalogue-appointment-types", practitionerId ?? null] as const,
};
